/**
 * ImportModal - CLZ / CSV collection import: upload -> preview -> resolve duplicates -> confirm.
 *
 * PLATFORM:F1.P9-10 Phase 1 & 2 / V-IMPORT. Extracted from VaultPage.jsx (where it lived inline
 * with its handlers behind `c8 ignore`) so the desktop modal and the mobile sheet run ONE state
 * machine.
 *
 * What changed against the inline version:
 *   - uploads go through fetchAPIMultipart (CSRF + Authorization), not a raw fetch;
 *   - the preview's `duplicates` list drives ImportConflictResolver BETWEEN preview and confirm.
 *     Resolutions are accumulated client-side (`{conflict_key: 'skip'|'overwrite'|'keep_both'}`)
 *     and submitted ONCE with `confirmImport(import_id, {resolutions, default_resolution})` — the
 *     old modal always sent `skip_duplicates: true`, so every other choice was fiction;
 *   - Cancel (and closing mid-preview) calls the new cancel route, releasing the one-active-import
 *     guard instead of leaving a `pending` log that 409-locked the user;
 *   - a 404 'expired' on confirm is its own state with a re-upload prompt.
 *
 * `variant='sheet'` renders as a bottom sheet for CollectionList (mobile); 'desktop' is the
 * centred modal VaultPage always had.
 */
import React, { useState, useEffect } from 'react';
import { Upload, X, Award } from 'lucide-react';
import ImportConflictResolver from './ImportConflictResolver';
import { uploadCLZFile, uploadCSVFile, confirmImport, cancelImport } from '../../services/vaultApi';

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

/**
 * The confirm route's 404 distinguishes an expired preview from an unknown import. The served
 * envelope is `{error_code: 'not_found', message, details: {code: 'expired'|'not_found'}}`
 * (app/core/error_handler.py); `details.code` is the machine-readable signal and the message
 * text ("Import preview expired. Please re-upload the file.") is the fallback for a caller that
 * only carried the flattened message.
 */
export function isExpiredImportError(err) {
  if (!err || err.status !== 404) return false;
  if (err.details && typeof err.details.code === 'string') return err.details.code === 'expired';
  return /expired/i.test(err.message || '');
}

export default function ImportModal({ isOpen, onClose, onImport, variant = 'desktop', initialType = 'clz' }) {
  const [file, setFile] = useState(null);
  const [importType, setImportType] = useState(initialType); // 'clz' or 'csv'
  const [step, setStep] = useState('upload'); // upload, preview, conflicts, importing, result, error, expired
  const [isLoading, setIsLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [resolutions, setResolutions] = useState({});
  const [defaultResolution, setDefaultResolution] = useState('skip');

  // Reset state when the modal closes (and pick up the host's requested type when it opens).
  useEffect(() => {
    if (!isOpen) {
      setFile(null);
      setStep('upload');
      setIsLoading(false);
      setPreview(null);
      setResult(null);
      setError(null);
      setDragOver(false);
      setResolutions({});
      setDefaultResolution('skip');
    }
    setImportType(initialType === 'csv' ? 'csv' : 'clz');
  }, [isOpen, initialType]);

  const handleFileSelect = (selectedFile) => {
    if (!selectedFile) return;
    if (!selectedFile.name.toLowerCase().endsWith('.csv')) {
      setError('Please select a CSV file.');
      return;
    }
    if (selectedFile.size > MAX_UPLOAD_BYTES) {
      setError('File exceeds 10MB limit.');
      return;
    }
    setError(null);
    setFile(selectedFile);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    handleFileSelect(droppedFile);
  };

  const handleUpload = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const uploadFn = importType === 'csv' ? uploadCSVFile : uploadCLZFile;
      const previewData = await uploadFn(file);
      setPreview(previewData);
      setResolutions({});
      setDefaultResolution('skip');
      setStep('preview');
    } catch (err) {
      setError(err.message || 'Upload failed');
      setStep('error');
    }
    setIsLoading(false);
  };

  const duplicates = preview && Array.isArray(preview.duplicates) ? preview.duplicates : [];

  const submitConfirm = async (finalResolutions, finalDefault) => {
    setStep('importing');
    try {
      const importResult = await confirmImport(preview.import_id, {
        resolutions: finalResolutions,
        default_resolution: finalDefault,
      });
      setResult(importResult);
      setStep('result');
      if (onImport) onImport(importResult);
    } catch (err) {
      if (isExpiredImportError(err)) {
        setStep('expired');
        return;
      }
      setError(err.message || 'Import failed');
      setStep('error');
    }
  };

  // "Confirm Import" on the preview: with duplicates, the resolver comes first; without, submit.
  const handleConfirm = () => {
    if (duplicates.length > 0) {
      setStep('conflicts');
      return;
    }
    submitConfirm(resolutions, defaultResolution);
  };

  const handleResolveOne = (dup, resolution) => {
    setResolutions((prev) => ({ ...prev, [dup.conflict_key]: resolution }));
  };

  const handleResolveAll = (unresolved, resolution) => {
    setDefaultResolution(resolution);
    setResolutions((prev) => {
      const next = { ...prev };
      unresolved.forEach((dup) => { next[dup.conflict_key] = resolution; });
      return next;
    });
  };

  // The resolver's Done (every conflict has a resolution): submit ONCE. Its X, with conflicts still
  // open, is an abort back to the preview — never a submit with choices the user did not make.
  const handleResolverClose = () => {
    const allResolved = duplicates.every((dup) => resolutions[dup.conflict_key] !== undefined);
    if (!allResolved) {
      setStep('preview');
      return;
    }
    submitConfirm(resolutions, defaultResolution);
  };

  // A previewed import holds the one-active-import slot server-side until it is confirmed or
  // cancelled. Cancelling is what releases it; silently dropping the preview used to leave it
  // `pending` forever.
  const releasePreview = () => {
    cancelImport(preview.import_id).catch(() => {
      // best effort: the server sweeps expired previews, and the user can only wait either way
    });
  };

  const handleCancelPreview = () => {
    releasePreview();
    setStep('upload');
    setPreview(null);
    setFile(null);
  };

  const handleClose = () => {
    if (step === 'preview' || step === 'conflicts') releasePreview();
    onClose();
  };

  const resetToUpload = () => {
    setStep('upload');
    setError(null);
    setFile(null);
    setPreview(null);
  };

  if (!isOpen) return null;

  const errorMsg = error ? <p className="text-red-400 text-sm mb-4">{error}</p> : null;
  const uploadLabel = isLoading ? 'Analyzing...' : 'Upload & Preview';
  const isSheet = variant === 'sheet';

  const shell = isSheet
    ? {
        overlay: 'fixed inset-0 bg-black/80 flex items-end justify-center z-50',
        panel: 'bg-zinc-900 border-t border-zinc-800 rounded-t-2xl w-full p-4 pb-8 max-h-[90vh] overflow-y-auto',
      }
    : {
        overlay: 'fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4',
        panel: 'bg-zinc-900 border border-zinc-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto',
      };

  return (
    <div className={shell.overlay} data-testid="import-modal" data-variant={variant}>
      <div className={shell.panel}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Import Collection</h2>
          <button onClick={handleClose} className="text-zinc-400 hover:text-white" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1: File Upload with Import Type Tabs */}
        {step === 'upload' && (
          <>
            <div className="flex gap-1 mb-4 bg-zinc-800/50 rounded-lg p-1" data-testid="import-type-tabs">
              <button
                onClick={() => { setImportType('clz'); setFile(null); setError(null); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  importType === 'clz' ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="import-tab-clz"
              >
                CLZ Import
              </button>
              <button
                onClick={() => { setImportType('csv'); setFile(null); setError(null); }}
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                  importType === 'csv' ? 'bg-orange-500 text-white' : 'text-zinc-400 hover:text-white'
                }`}
                data-testid="import-tab-csv"
              >
                CSV Import
              </button>
            </div>

            <p className="text-zinc-400 text-sm mb-4">
              {importType === 'clz'
                ? 'Upload your CLZ Comic Collector CSV export to import your collection.'
                : 'Upload any CSV file with comic data. Columns will be auto-detected.'
              }
            </p>

            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
                dragOver ? 'border-orange-500 bg-orange-500/10' : 'border-zinc-700'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              data-testid="clz-upload-dropzone"
            >
              <input
                type="file"
                accept=".csv"
                onChange={(e) => handleFileSelect(e.target.files?.[0])}
                className="hidden"
                id="import-file-upload"
              />
              <label htmlFor="import-file-upload" className="cursor-pointer flex flex-col items-center">
                <Upload className="w-10 h-10 text-zinc-500 mb-2" />
                <span className="text-zinc-400">
                  {file ? file.name : 'Drag & drop or click to select CSV file'}
                </span>
                {file && (
                  <span className="text-zinc-500 text-xs mt-1">
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
              </label>
            </div>

            {errorMsg}

            <button
              onClick={handleUpload}
              disabled={!file || isLoading}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
              data-testid="clz-upload-button"
            >
              {uploadLabel}
            </button>
          </>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && preview && (
          <>
            <div className="space-y-4">
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-3">Import Preview</h3>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-2xl font-bold text-white">{preview.rows_detected}</p>
                    <p className="text-xs text-zinc-500">Total Rows</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-green-400">
                      {preview.match_statistics.estimated_matches || 0}
                    </p>
                    <p className="text-xs text-zinc-500">Est. Matches</p>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-yellow-400" data-testid="preview-duplicates">
                      {duplicates.length}
                    </p>
                    <p className="text-xs text-zinc-500">Duplicates</p>
                  </div>
                </div>
              </div>

              {preview.fields_mapped.length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-zinc-300 mb-2">Field Mapping</h3>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {preview.fields_mapped.map((fm, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className="text-zinc-400">{fm.csv_column}</span>
                        <span className="text-zinc-600 mx-2">&rarr;</span>
                        <span className="text-orange-400">{fm.vault_field}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {preview.sample_rows.length > 0 && (
                <div className="bg-zinc-800/50 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-zinc-300 mb-2">
                    Sample Data ({preview.sample_rows.length} rows)
                  </h3>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {preview.sample_rows.map((row, i) => (
                      <div key={i} className="text-xs bg-zinc-900 rounded p-2">
                        <span className="text-white font-medium">
                          {row.title || 'Untitled'}
                        </span>
                        {row.issue_number && (
                          <span className="text-zinc-400"> #{row.issue_number}</span>
                        )}
                        {row.publisher && (
                          <span className="text-zinc-500"> ({row.publisher})</span>
                        )}
                        {row.user_grade && (
                          <span className="text-green-400 ml-2">{row.user_grade}</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {duplicates.length > 0 && (
                <p className="text-xs text-yellow-300/90" data-testid="preview-duplicates-note">
                  {duplicates.length} row{duplicates.length === 1 ? '' : 's'} already in your vault —
                  you will choose what to do with each before anything is written.
                </p>
              )}
            </div>

            <div className="flex gap-3 mt-4">
              <button
                onClick={handleCancelPreview}
                className="flex-1 py-3 bg-zinc-800 hover:bg-zinc-700 text-white font-medium rounded-lg transition-colors"
                data-testid="clz-cancel-button"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
                data-testid="clz-confirm-button"
              >
                {duplicates.length > 0 ? 'Resolve Duplicates' : 'Confirm Import'}
              </button>
            </div>
          </>
        )}

        {/* Step 2b: duplicate resolution, before anything is written */}
        {step === 'conflicts' && (
          <ImportConflictResolver
            isOpen
            onClose={handleResolverClose}
            duplicates={duplicates}
            onResolve={handleResolveOne}
            onResolveAll={handleResolveAll}
          />
        )}

        {/* Step 3: Importing */}
        {step === 'importing' && (
          <div className="text-center py-8">
            <div className="animate-spin w-12 h-12 border-4 border-zinc-700 border-t-orange-500 rounded-full mx-auto mb-4" />
            <p className="text-white font-medium mb-2">Importing your collection...</p>
            <p className="text-zinc-400 text-sm">This may take a moment for large files.</p>
          </div>
        )}

        {/* Step 4: Result */}
        {step === 'result' && result && (
          <div className="space-y-4" data-testid="import-result">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
                <Award className="w-8 h-8 text-green-400" />
              </div>
              <h3 className="text-lg font-bold text-white mb-1">Import Complete!</h3>
              {result.points_awarded > 0 && (
                <p className="text-orange-400 text-sm font-medium">
                  +{result.points_awarded} points earned
                </p>
              )}
            </div>

            <div className="bg-zinc-800/50 rounded-lg p-4">
              <div className="grid grid-cols-2 gap-3 text-center">
                <div>
                  <p className="text-xl font-bold text-green-400">{result.rows_imported}</p>
                  <p className="text-xs text-zinc-500">Imported</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-400">{result.rows_matched}</p>
                  <p className="text-xs text-zinc-500">Matched</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-yellow-400">{result.rows_skipped}</p>
                  <p className="text-xs text-zinc-500">Skipped</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-red-400">{result.rows_failed}</p>
                  <p className="text-xs text-zinc-500">Failed</p>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        )}

        {/* Expired preview */}
        {step === 'expired' && (
          <div className="text-center py-4" data-testid="import-expired">
            <p className="text-yellow-400 font-medium mb-2">Import preview expired</p>
            <p className="text-zinc-400 text-sm mb-4">
              Nothing was written. Upload the file again to get a fresh preview.
            </p>
            <button
              onClick={resetToUpload}
              className="text-orange-400 hover:text-orange-300 font-medium"
              data-testid="import-reupload"
            >
              Upload again
            </button>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-3">
              <X className="w-8 h-8 text-red-400" />
            </div>
            <p className="text-red-400 font-medium mb-2">Import Failed</p>
            <p className="text-zinc-400 text-sm mb-4">{error}</p>
            <button
              onClick={resetToUpload}
              className="text-orange-400 hover:text-orange-300 font-medium"
            >
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
